import CheckBox from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlank from '@mui/icons-material/CheckBoxOutlineBlank';
import MailOutline from '@mui/icons-material/MailOutline';
import Smartphone from '@mui/icons-material/Smartphone';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import { Nullable } from '~types/Nullable';

export type InviteType = 'email' | 'sms' | 'none';

interface UserInviteTypeSelectorProps {
  descriptionKey?: string;
  email: Nullable<string>;
  error?: string;
  isDisabled?: boolean;
  isEmailEnabled?: boolean;
  isPhoneEnabled?: boolean;
  onChange: (value: InviteType[]) => void;
  phone: Nullable<string>;
  titleKey?: string;
  value: InviteType[];
}

interface InviteOption {
  destination?: string;
  icon: React.ReactNode;
  subtitle: string;
  title: string;
  value: Exclude<InviteType, 'none'>;
}

export default function UserInviteTypeSelector({
  descriptionKey = 'invite_type.description',
  email,
  error,
  isDisabled,
  isEmailEnabled,
  isPhoneEnabled,
  onChange,
  phone,
  titleKey = 'invite_type.title_user',
  value,
}: UserInviteTypeSelectorProps) {
  const { brandV2, palette } = useTheme();
  const { colors, borderRadius } = brandV2;

  const disabled = Boolean(isDisabled);
  const isSkipped = value.includes('none');

  const options: InviteOption[] = [
    {
      value: 'email',
      title: t('invite_type.email_title'),
      subtitle: t('invite_type.email_subtitle'),
      icon: <MailOutline sx={{ fontSize: '18px' }} />,
      destination: isEmailEnabled ? (email ?? undefined) : undefined,
    },
    {
      value: 'sms',
      title: t('invite_type.sms_title'),
      subtitle: t('invite_type.sms_subtitle'),
      icon: <Smartphone sx={{ fontSize: '18px' }} />,
      destination: isPhoneEnabled ? (phone ?? undefined) : undefined,
    },
  ];

  const handleToggle = (optionValue: Exclude<InviteType, 'none'>) => {
    if (disabled) return;

    const isSelected = value.includes(optionValue);
    const next = isSelected
      ? value.filter((v) => v !== optionValue)
      : [...value.filter((v) => v !== 'none'), optionValue];

    onChange(next);
  };

  const handleSkipToggle = () => {
    if (disabled) return;
    onChange(isSkipped ? [] : ['none']);
  };

  return (
    <Box
      sx={{
        backgroundColor: colors.treadGray9,
        border: `2px solid ${error ? palette.error.main : palette.primary.main}`,
        borderRadius,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 1.5,
        width: '100%',
      }}
    >
      <Typography sx={{ color: colors.treadBlack, fontSize: '13px', fontWeight: 700 }}>
        {t(titleKey)} *
      </Typography>

      <Typography variant="caption" sx={{ color: colors.treadGray2 }}>
        {t(descriptionKey)}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gap: 1,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
        }}
      >
        {options.map((option) => {
          const isSelected = value.includes(option.value);

          return (
            <Box
              key={option.value}
              onClick={disabled ? undefined : () => handleToggle(option.value)}
              onKeyDown={(event) => {
                if (disabled) return;
                if (event.key === ' ' || event.key === 'Enter') {
                  event.preventDefault();
                  handleToggle(option.value);
                }
              }}
              role="checkbox"
              aria-checked={isSelected}
              aria-disabled={disabled}
              tabIndex={disabled ? -1 : 0}
              sx={{
                backgroundColor: isSelected
                  ? alpha(colors.treadOrange, 0.15)
                  : colors.treadGray10,
                border: `1px solid ${
                  isSelected ? colors.treadOrange : colors.treadGray7
                }`,
                borderRadius,
                cursor: disabled ? 'not-allowed' : 'pointer',
                display: 'flex',
                gap: 0.5,
                opacity: disabled ? 0.6 : 1,
                p: 1,
              }}
            >
              {isSelected ? (
                <CheckBox sx={{ color: colors.treadOrange, fontSize: '18px' }} />
              ) : (
                <CheckBoxOutlineBlank
                  sx={{ color: colors.treadGray5, fontSize: '18px' }}
                />
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
                  {option.icon}
                  <Typography
                    sx={{
                      color: colors.treadBlack,
                      fontSize: '13px',
                      fontWeight: 700,
                    }}
                  >
                    {option.title}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: colors.treadGray2 }}>
                  {option.subtitle}
                </Typography>
                {option.destination && (
                  <Typography
                    variant="caption"
                    sx={{ color: colors.treadGray3, fontStyle: 'italic' }}
                  >
                    {option.destination}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={isSkipped}
            disabled={disabled}
            onChange={handleSkipToggle}
            sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '16px' } }}
          />
        }
        label={
          <Typography
            sx={{
              color: colors.treadGray3,
              fontSize: '11px',
              fontWeight: 400,
            }}
          >
            {t('invite_type.none_title')}
          </Typography>
        }
        sx={{ m: 0, ml: -0.5, alignSelf: 'flex-start', gap: 0.5 }}
      />

      {error && (
        <FormHelperText error sx={{ m: 0, fontSize: '10px', fontWeight: 700 }}>
          {error}
        </FormHelperText>
      )}
    </Box>
  );
}
